/**
 * `/campaigns/new` — one page: pick a template, pick a segment, pick a time.
 *
 * **It is one page and it creates a draft. It does not send.** That separation
 * is the whole shape of the screen, and it is the same one slice 2 chose: the
 * create screen shows every number it can honestly know, saves, and the thing's
 * own screen shows the numbers only the server can compute. Here those numbers
 * are the ones that matter most — how many of these people the send guard will
 * actually let through, and everything `campaignScheduleIssues` has to say about
 * the template, the sending account and the time. None of them can be computed
 * until the campaign exists, because they are all answers about *this* campaign
 * against *this* workspace's SES account and allow-list.
 *
 * So the flow is: fill this in, land on the campaign, read what will actually
 * happen, and schedule from there. That is not a wizard — there is no step
 * state, nothing is stored half-made, and every field on this page is
 * independent of every other. It is a form that saves, followed by a decision
 * taken with the facts in view.
 *
 * **The reach appears the moment a segment is chosen**, and it is the channel
 * reach: a segment of 1,278 people reaches 526 by email, and SLICES names
 * showing the segment total here as *the* failure mode, because it lets someone
 * commit believing they are reaching twice as many people as they are. It is
 * derived with `campaignReach()` from `@wend/shared` — the only supported way to
 * get that number, so this screen cannot accidentally print a different one from
 * the API's.
 *
 * **Slice 7 makes the screen channel-driven rather than adding a second one.** A
 * WhatsApp campaign is the same three choices against a different channel, so
 * the channel is a field and everything downstream of it — which templates are
 * listed, which reach is shown, whether a from-address exists at all — reads
 * that field. The reach in particular: on WhatsApp it is the phone-reachable
 * count, which on the real segment is materially smaller than the email one, and
 * printing either channel's figure on the other is the overstatement slice 2
 * settled.
 *
 * **Only approved templates are offered.** The contract says so
 * (`templateListQuerySchema.status` — "the campaign wizard in slice 5 lists
 * `approved` only") and the API refuses a draft template at schedule time
 * anyway. Offering one here would mean building a campaign that cannot be sent
 * and finding out two screens later.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * SLICE 17 — PROGRESSIVE, NOT SHORTER AND VAGUER
 * ────────────────────────────────────────────────────────────────────────────
 *
 * This is the highest-stakes instruction surface in Reach: somebody filling it
 * in is assembling a message to a list of real cooperative banks. Every
 * paragraph on it was therefore examined for which of three things it was doing,
 * and the answer decided where it went rather than whether it was long.
 *
 * **The reach sentence got shorter and stronger.** It used to explain, in the
 * empty state, that a campaign reaches only the people it can reach on its own
 * channel and that Wend never falls back to the other. True, and an argument.
 * What it never said was *what the number is*, and the number is the one this
 * whole page exists to keep honest: it now says "the distinct email addresses in
 * the segment", from `REACH_ADDRESS_PLURAL`, which is the phrase the reach panel
 * and the campaign's own screen use. The argument moved four inches down into
 * the disclosure beside it, where the questions it answers — what about somebody
 * on two lists, why is the WhatsApp figure smaller — are asked once and then
 * never again.
 *
 * **And it is on both branches of the card, which is the correction
 * `verify-page-hardening.tsx` was written to catch.** The first version put the
 * definition only in the paragraph that shows while no segment is chosen — and
 * `<CampaignReachPanel>` *replaces* that paragraph, so the words "distinct email
 * addresses" left the screen at precisely the moment a number arrived for them
 * to be about. Nothing looked wrong: every other sentence on the card was still
 * right, and the only remaining occurrence of the word was inside the disclosure
 * beside it, folded. A definition that is present only while there is nothing to
 * define is a definition nobody reads, and the rule against folding a correction
 * away is not satisfied by a copy that is conditionally rendered instead.
 *
 * So the header carries it whenever there *is* a figure, in the same words as
 * `/campaigns/:id`, and the paragraph carries it while there is not. Exactly one
 * of the two is on screen at any moment — a `CardDescription` rendered
 * unconditionally would sit directly above the paragraph saying the same thing
 * twice, which is the padding this slice exists to remove.
 *
 * **`AdvancedDisclosure` is slice 16's, imported rather than re-implemented.**
 * It lives in `routes/integrations` and this file already reaches into that
 * folder for `integrationTabPath`, so the import is not a new coupling; a second
 * `<details>` recipe on a second route would be, and slice 16 spent itself
 * undoing exactly that kind of accretion. Its `label` is `"Detail"` here rather
 * than `"Advanced"`, because nothing inside it is a setting. **Both that
 * component and `routes/templates/MoreDetail.tsx` belong in `components/`** —
 * recorded in the slice report rather than done here, because moving a file
 * under three agents working in parallel is a merge nobody asked for.
 *
 * **Nothing that corrects a misreading went into the disclosure.** A skimmer
 * never opens one, which is the whole reason the rule sorts text this way, so
 * the definition of reach, "creates a draft and sends nothing", the quiet-hours
 * warning and the from-address rule are all still on the screen unfolded. What
 * is behind the summary is only ever the second question.
 *
 * ## Two load failures that were being reported as facts about the workspace
 *
 * Both are the "zero versus absent" defect wearing different clothes, and both
 * were found by asking what each of these three reads renders when it fails.
 *
 * **The sending-account read was the serious one.** `connected` is
 * `integration.data !== null`, and that is null both when no SES account is
 * connected and when the request did not come back — so a workspace with a
 * perfectly good production account, on a flaky connection, was told it had no
 * sending account and shown a link to go and connect one. It now has its own
 * branch, checked first, rendering `ErrorState` with the request id and a retry.
 *
 * **The template and segment pickers failed into a dead end.** "Your templates
 * could not be loaded. Reload the page and try again" carried no request id, no
 * server message and no retry — and reloading the page is precisely the advice
 * that loses everything already typed into this form. Both now render
 * `ErrorState`, which is the component for a screen that could not load what it
 * went to fetch, and `useAsyncData`'s own `reload` re-runs just that request.
 * `FormAlert` stays where it belongs, on the submission's verdict at the top.
 */
import {
  MAX_CAMPAIGN_NAME_LENGTH,
  MAX_PAGE_SIZE,
  campaignReach,
  createCampaignRequestSchema,
  isQuietHour,
  type CampaignReach,
  type Channel,
  type SegmentSummary,
  type SesIdentity,
  type TemplateSummary,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Kicker,
  Select,
  Spinner,
  buttonVariants,
  useToast,
  type SelectOption,
} from '@wend/ui';
import { ArrowLeft, Clock, Plus } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { CampaignReachPanel } from '../../components/CampaignReach';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { SendGuardAlwaysOnNotice } from '../../components/SendGuardNotice';
import { createCampaign } from '../../lib/api/campaigns';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { fetchSesIntegration } from '../../lib/api/integrations';
import { fetchSegment, listSegments } from '../../lib/api/segments';
import { listTemplates } from '../../lib/api/templates';
import {
  CHANNEL_LABEL,
  browserTimeZone,
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
} from '../../lib/campaign-display';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { formatCount, pluralise } from '../../lib/format';
import { CHECK_FAILED_DISPLAY, SES_NOT_TESTED, identityTypeLabel } from '../../lib/integration-display';
import {
  asPickableSegment,
  offeredSegments,
  prefilledSegmentId,
  type PickableSegment,
} from '../../lib/segment-prefill';
import { useAsyncData } from '../../lib/use-async-data';
import { AdvancedDisclosure } from '../integrations/AdvancedDisclosure';
import { integrationTabPath } from '../integrations/integration-tab-url';
import { REACH_ADDRESS_PLURAL } from './reach-words';

/** `null` scheduledAt on the draft; the campaign screen offers a time. */
type Timing = 'later' | 'at';

/**
 * Whether this build's contract accepts a WhatsApp campaign — **asked of the
 * contract, not remembered here**.
 *
 * `createCampaignRequestSchema` is the authority on what the API will take, and
 * for most of v1 its `channel` was `z.literal('email')`. A channel picker
 * offering WhatsApp against that contract would be a control that always fails,
 * and a picker hard-disabled by a boolean written here would keep saying "not
 * yet" for a week after the contract widened. So the screen asks: a probe body
 * that differs from a valid email one only in the channel. Everything else on
 * this page is channel-driven regardless, so the day the contract widens, this
 * flips on with no edit.
 */
const WHATSAPP_CAMPAIGNS_SUPPORTED = createCampaignRequestSchema.safeParse({
  name: 'probe',
  channel: 'whatsapp',
  templateId: '00000000-0000-4000-8000-000000000000',
  segmentId: '00000000-0000-4000-8000-000000000000',
}).success;

export function NewCampaignPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Resolved once per mount: the wall clock somebody types below is interpreted
  // by the platform in this zone, so this is the only zone the campaign can
  // honestly claim its time was chosen in.
  const zone = useMemo(browserTimeZone, []);

  const [channel, setChannel] = useState<Channel>('email');
  const [name, setName] = useState('');
  /*
   * SLICE 34: THIS FORM CAN NOW BE OPENED WITH A TEMPLATE ALREADY CHOSEN.
   *
   * `/campaigns/new?template=<id>` is what the Overview's "Start from a
   * template" card links to. Until this slice the form read no query parameter
   * at all, and several comments across the app said so as a settled fact —
   * `CalendarCard`'s among them, which is why its rows link to a blank form and
   * claim nothing. Those comments are now wrong for templates and right for
   * everything else; the ones that matter have been narrowed rather than left
   * to argue the next reader into deleting this.
   *
   * **The parameter is a REQUEST, not a value.** It is honoured only when it
   * names a row in the list this form actually offers — approved, of this
   * channel — because `template` at the `find` below is what feeds the reach
   * panel and the review step, and `templateId` is what gets submitted. Seeding
   * the raw id would let `/campaigns/new?template=<a draft>` submit an id the
   * server then refuses with `template_not_approved`, reported as a form error
   * on a field the reader never touched. An id that is not on offer simply
   * never takes, and the reader meets an ordinary empty picker.
   *
   * It is DERIVED rather than pushed into state by an effect. The list arrives
   * asynchronously, so an effect would need a "have I already seeded" flag, and
   * that flag is the bug: it makes clearing the field a state the component can
   * re-seed out of. Here the reader's first interaction sets `touched` and
   * their choice wins permanently after that, including their choice of none.
   */
  const [searchParams] = useSearchParams();
  const requestedTemplateId = searchParams.get('template');
  const [chosenTemplateId, setChosenTemplateId] = useState<string | null>(null);
  const [templateTouched, setTemplateTouched] = useState(false);
  /*
   * SLICE 50: AND WITH A SEGMENT ALREADY CHOSEN, WHICH IS THE OTHER HALF.
   *
   * `/campaigns/new?segment=<id>` is where the Overview's "Worth doing next"
   * card goes once one of its counts has been saved as a cohort, so the button
   * beside "1,284 people have not heard from you in 90 days" finally leads to
   * that number's audience.
   *
   * Same rule as the template above — a REQUEST, honoured only when it names
   * something this form is offering, derived rather than seeded by an effect —
   * and one addition it needs that the template does not. The segment list is
   * one page of a hundred by name, and a cohort's name carries the day it was
   * counted, so on a workspace with more than a hundred segments the one thing
   * the URL asked for can fall outside the page and the prefill silently does
   * nothing for exactly the person who has just built the audience. So the
   * requested segment is also read on its own and merged into the options; see
   * `lib/segment-prefill.ts`, where both decisions live as functions a suite can
   * run, because this page cannot be mounted by one.
   */
  const requestedSegmentId = searchParams.get('segment');
  const [chosenSegmentId, setChosenSegmentId] = useState<string | null>(null);
  const [segmentTouched, setSegmentTouched] = useState(false);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [timing, setTiming] = useState<Timing>('later');
  const [when, setWhen] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const templates = useAsyncData(
    (signal) =>
      listTemplates(
        {
          page: 1,
          pageSize: MAX_PAGE_SIZE,
          sort: 'updatedAt',
          direction: 'desc',
          type: channel,
          status: 'approved',
        },
        signal,
      ),
    [channel],
  );

  const segments = useAsyncData(
    (signal) =>
      listSegments({ page: 1, pageSize: MAX_PAGE_SIZE, sort: 'name', direction: 'asc' }, signal),
    [],
  );

  /*
   * The one segment the URL asked for, read on its own.
   *
   * Only when there is one to read: with no `?segment=` this resolves to null
   * without a request. A failure resolves to null too rather than rejecting,
   * which is the same answer as "that id is not on offer" — a deleted or
   * borrowed id leaves the reader at an ordinary empty picker, and turning it
   * into an error state would put a failed read on a form whose own list loaded
   * perfectly. Nothing here is retried, because there is nothing a reader could
   * do about it on this screen. An abort is not one of those failures: the hook
   * discards the answer to a request it aborted before touching state, so
   * resolving to null here cannot leave a stale prefill behind.
   */
  const requestedSegment = useAsyncData(
    async (signal) =>
      requestedSegmentId === null
        ? null
        : fetchSegment(requestedSegmentId, signal).then(asPickableSegment, () => null),
    [requestedSegmentId],
  );

  // The sending account, for one purpose: naming the identities AWS has actually
  // verified. `from_address_not_verified` is otherwise a blocker discovered two
  // screens later, and the repair — knowing which addresses are allowed — is
  // exactly what this read supplies.
  const integration = useAsyncData((signal) => fetchSesIntegration(signal), []);

  const offeredTemplates = templates.data?.items ?? [];
  const requestHonoured =
    !templateTouched &&
    requestedTemplateId !== null &&
    offeredTemplates.some((row) => row.id === requestedTemplateId);
  const templateId = requestHonoured ? requestedTemplateId : chosenTemplateId;

  const template = offeredTemplates.find((row) => row.id === templateId) ?? null;

  // The listed page plus, when it is not already in it, the one the URL asked
  // for. Both decisions are in `lib/segment-prefill.ts` and are asserted by
  // running them; see that file's header for why they are not inline here.
  const offered = offeredSegments(segments.data?.items ?? [], requestedSegment.data ?? null);
  const segmentId = prefilledSegmentId({
    requested: requestedSegmentId,
    touched: segmentTouched,
    chosen: chosenSegmentId,
    offered,
  });
  const segment = offered.find((row) => row.id === segmentId) ?? null;

  /**
   * The only supported derivation. A segment total here would be the failure
   * mode SLICES names; `campaignReach` cannot produce one.
   *
   * **The channel is the argument, not a constant.** On WhatsApp this is the
   * phone-reachable count — the people in the segment carrying a number — and it
   * is a smaller figure than the email one on the same segment. Showing the
   * segment's size, or the other channel's reach, is exactly the overstatement
   * slice 2 settled: a campaign reaches only the people it can reach on its own
   * channel, and Wend never falls back to another to make the number bigger.
   */
  const reach: CampaignReach | null =
    segment === null ? null : campaignReach(segment.counts, channel);

  const scheduledAt = timing === 'at' ? fromDateTimeLocalValue(when) : null;
  const quietHours =
    scheduledAt !== null && isQuietHour(scheduledAt, zone.timezone);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setRequestId(undefined);

    // The two ids are checked before the contract runs, because the contract's
    // own message for a missing one is "Invalid uuid" — true, and useless to
    // somebody who simply has not chosen a template yet.
    const missing: FormErrors = {};
    if (templateId === null) {
      missing.templateId =
        channel === 'whatsapp'
          ? 'Choose the WhatsApp template this campaign sends.'
          : 'Choose the email this campaign sends.';
    }
    if (segmentId === null) missing.segmentId = 'Choose who it goes to.';
    if (timing === 'at' && scheduledAt === null) {
      missing.scheduledAt = 'Pick a date and a time, or choose to decide later.';
    }
    if (Object.keys(missing).length > 0) {
      setErrors((current) => ({ ...current, ...missing }));
      return;
    }

    // A WhatsApp campaign has no from-address: the sender is the number on the
    // connected Meta account, which is a property of the integration rather than
    // something a campaign chooses. The fields are therefore omitted rather than
    // sent empty — the contract is `.strict()`, and an empty string is not an
    // absent value.
    const result = validate(createCampaignRequestSchema, {
      name,
      channel,
      templateId,
      segmentId,
      ...(channel === 'email' ? { fromEmail, fromName } : {}),
      scheduledAt: scheduledAt === null ? null : scheduledAt.toISOString(),
      timezone: zone.timezone,
    });

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const campaign = await createCampaign(result.data);
      toast({
        tone: 'success',
        title: 'Draft created',
        description: 'Nothing has been sent. Review it and schedule when you are ready.',
      });
      // Straight to the campaign, because the two numbers this screen could not
      // work out — how many will actually be delivered, and what still stops it
      // being scheduled — are waiting there.
      navigate(`/campaigns/${campaign.id}`, { replace: true });
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  return (
    <AppLayout
      title="New campaign"
      actions={
        <Link to="/campaigns" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <ArrowLeft aria-hidden="true" className="size-4" />
          All campaigns
        </Link>
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        noValidate
        className="flex flex-col gap-6"
      >
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_23rem] lg:items-start">
          <div className="flex min-w-0 flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>What is being sent, and to whom</CardTitle>
                <CardDescription>
                  One approved template to one segment, one message per person.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex max-w-xl flex-col gap-5">
                {WHATSAPP_CAMPAIGNS_SUPPORTED && (
                  <FormField
                    label="Channel"
                    required
                    description="Changing this changes who the campaign can reach, and the two counts differ."
                  >
                    <Select
                      value={channel}
                      disabled={pending}
                      options={[
                        { value: 'email', label: 'Email — through your Amazon SES account' },
                        { value: 'whatsapp', label: 'WhatsApp — through your Meta account' },
                      ]}
                      onValueChange={(value) => {
                        setChannel(value === 'whatsapp' ? 'whatsapp' : 'email');
                        // The template list is per channel, so a template chosen
                        // on the other one is no longer a choice that exists.
                        // `touched` is set with it: a `?template=` in the URL
                        // named a template of the channel being left, and
                        // re-seeding it here would hand this channel a template
                        // belonging to the other one the moment its list
                        // happened to contain that id.
                        setTemplateTouched(true);
                        setChosenTemplateId(null);
                        setErrors({});
                      }}
                    />
                  </FormField>
                )}

                <FormField label="Campaign name" required error={errors.name}>
                  <Input
                    value={name}
                    autoFocus
                    maxLength={MAX_CAMPAIGN_NAME_LENGTH}
                    placeholder="Diwali offer — cooperative banks"
                    disabled={pending}
                    onChange={(event) => {
                      setName(event.target.value);
                      setErrors((current) => withoutError(current, 'name'));
                    }}
                  />
                </FormField>

                <TemplateField
                  channel={channel}
                  templates={templates.data?.items ?? []}
                  total={templates.data?.pagination.total ?? 0}
                  loading={templates.loadingFirst}
                  // Null unless the load failed AND left nothing to show. A
                  // refresh that fails over a list already on screen keeps the
                  // list; see the header.
                  failure={templates.data === null ? templates.error : null}
                  onRetry={templates.reload}
                  value={templateId}
                  error={errors.templateId}
                  disabled={pending}
                  onChange={(value) => {
                    setTemplateTouched(true);
                    setChosenTemplateId(value);
                    setErrors((current) => withoutError(current, 'templateId'));
                  }}
                />

                <SegmentField
                  channel={channel}
                  segments={offered}
                  /*
                    The list read's own total, which is what the field's "the
                    first N by name are listed, of M" line is about. A merged-in
                    segment is one the reader asked for by id rather than one
                    more row of the page, so it does not move that count.
                  */
                  total={segments.data?.pagination.total ?? 0}
                  /*
                    Both reads, when the URL asked for one. The field is worth
                    withholding for the extra read's sake: drawn early it would
                    offer an empty picker for a moment, and a reader who touches
                    it in that moment owns the field permanently — so the prefill
                    they came here for would be discarded by their own click.
                    The two requests start together, so in the ordinary case this
                    waits for nothing.
                  */
                  loading={
                    segments.loadingFirst ||
                    (requestedSegmentId !== null && requestedSegment.loadingFirst)
                  }
                  failure={segments.data === null ? segments.error : null}
                  onRetry={segments.reload}
                  value={segmentId}
                  error={errors.segmentId}
                  disabled={pending}
                  onChange={(value) => {
                    setSegmentTouched(true);
                    setChosenSegmentId(value);
                    setErrors((current) => withoutError(current, 'segmentId'));
                  }}
                />
              </CardContent>
            </Card>

            {channel === 'whatsapp' ? (
              <Card>
                <CardHeader>
                  <CardTitle>Who it comes from</CardTitle>
                  <CardDescription>
                    Nothing to choose: a WhatsApp message comes from the number on your connected
                    Meta account, under the display name Meta verified for it.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link
                    to={integrationTabPath('whatsapp')}
                    className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                  >
                    Check the WhatsApp connection
                  </Link>
                </CardContent>
              </Card>
            ) : (
            <Card>
              <CardHeader>
                <CardTitle>Who it comes from</CardTitle>
                <CardDescription>
                  The address recipients see and reply to. Amazon refuses anything your own AWS
                  account has not verified, and Wend cannot verify one for you.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex max-w-xl flex-col gap-5">
                <FormField label="From address" required error={errors.fromEmail}>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    value={fromEmail}
                    placeholder="hello@yourdomain.in"
                    disabled={pending}
                    onChange={(event) => {
                      setFromEmail(event.target.value);
                      setErrors((current) => withoutError(current, 'fromEmail'));
                    }}
                  />
                </FormField>

                <VerifiedIdentities
                  identities={integration.data?.lastCheck?.identities ?? null}
                  // Asked separately, because the line above cannot tell the
                  // two nulls apart: a check that never came back, and a check
                  // whose key was refused the identity listing.
                  checked={(integration.data?.lastCheck ?? null) !== null}
                  lastCheckFailed={integration.data?.status === 'failed'}
                  connected={integration.data !== null}
                  loading={integration.loadingFirst}
                  // A request that never came back says nothing about whether an
                  // account is connected. Without this the panel below read the
                  // failure as "not connected" and told a workspace with a
                  // working SES account to go and connect one.
                  failure={integration.data === null ? integration.error : null}
                  onRetry={integration.reload}
                  disabled={pending}
                  onUse={(address) => {
                    setFromEmail(address);
                    setErrors((current) => withoutError(current, 'fromEmail'));
                  }}
                />

                <FormField
                  label="From name"
                  description="Optional. Shown beside the address in most mail clients."
                  error={errors.fromName}
                >
                  <Input
                    value={fromName}
                    placeholder="Acme Retail"
                    disabled={pending}
                    onChange={(event) => {
                      setFromName(event.target.value);
                      setErrors((current) => withoutError(current, 'fromName'));
                    }}
                  />
                </FormField>
              </CardContent>
            </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>When it goes out</CardTitle>
                <CardDescription>
                  A time here schedules nothing — it is carried onto the draft. Nothing is built or
                  sent until you press Schedule on the campaign itself.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex max-w-xl flex-col gap-5">
                <fieldset className="flex flex-col gap-2.5" disabled={pending}>
                  <legend className="mb-1.5 text-[0.85rem] font-medium text-text">Timing</legend>

                  <TimingChoice
                    checked={timing === 'later'}
                    onSelect={() => {
                      setTiming('later');
                      setErrors((current) => withoutError(current, 'scheduledAt'));
                    }}
                    label="Decide on the next screen"
                    description="The draft carries no time. You pick one when you schedule it, which is also when you can send it straight away."
                  />

                  <TimingChoice
                    checked={timing === 'at'}
                    onSelect={() => {
                      setTiming('at');
                    }}
                    label="At a specific time"
                    description={`In ${zone.timezone}${zone.resolved ? '' : ' — this browser would not say which timezone it is in, so Wend has assumed the default'}.`}
                  />
                </fieldset>

                {timing === 'at' && (
                  <FormField
                    label="Date and time"
                    description={`Interpreted in ${zone.timezone}, and stored with the campaign so it always reads back in the zone it was chosen in.`}
                    error={errors.scheduledAt}
                  >
                    <Input
                      type="datetime-local"
                      value={when}
                      min={toDateTimeLocalValue(new Date())}
                      disabled={pending}
                      onChange={(event) => {
                        setWhen(event.target.value);
                        setErrors((current) => withoutError(current, 'scheduledAt'));
                      }}
                    />
                  </FormField>
                )}

                {quietHours && (
                  <Callout tone="warn" title="That lands in the middle of the night" live="polite">
                    {channel === 'whatsapp'
                      ? 'A message that wakes somebody up gets blocked, and blocks are what Meta rates your number on — a red rating cuts your messaging limit and can pause the number. A warning, not a refusal; you will be reminded once more before you schedule.'
                      : 'Marketing mail delivered overnight draws more spam complaints, and complaints are what get a sending account suspended — under BYO, your own AWS account. A warning, not a refusal; you will be reminded once more before you schedule.'}
                  </Callout>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-6 lg:sticky lg:top-20">
            <Card>
              <CardHeader>
                <CardTitle className="text-[1rem]">Who this reaches</CardTitle>
                {/*
                  THE DEFINITION IS ON WHICHEVER BRANCH IS ON SCREEN, AND IT USED
                  TO BE ON ONLY ONE OF THEM.

                  It lived in the empty-state paragraph below — and
                  `CampaignReachPanel` replaces that paragraph, so the words
                  "distinct email addresses" left the screen at exactly the
                  moment a number arrived for them to be about. The only
                  remaining copy was inside the disclosure, folded. Here it is
                  the header's description, rendered when there is a figure to
                  describe; the paragraph below says the same thing while there
                  is not. One or the other is always on screen and never both,
                  and both take the noun from `REACH_ADDRESS_PLURAL`, which is
                  also where `/campaigns/:id` gets it.
                */}
                {reach !== null && (
                  <CardDescription>
                    Reach is the distinct {REACH_ADDRESS_PLURAL[channel]} in the segment, never the
                    size of the segment.
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="flex flex-col gap-5">
                {reach === null ? (
                  <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">
                    Choose a segment and its reach appears here: the distinct{' '}
                    <span className="text-text">{REACH_ADDRESS_PLURAL[channel]}</span> in it — not
                    the number of contacts, and not the size of the segment.
                  </p>
                ) : (
                  <CampaignReachPanel reach={reach} />
                )}

                {/*
                 * The instruction, folded — slice 17's third kind of text.
                 *
                 * Everything in here was previously a paragraph on the screen,
                 * and every sentence of it is an answer to a question somebody
                 * asks once: what happens to a contact on two lists, why the
                 * WhatsApp figure is smaller, why Wend does not use the other
                 * channel's address. **What is deliberately NOT in here is the
                 * definition of reach itself** — that is the number this page is
                 * read for and the one most easily overstated, so it stays above
                 * in the words the rule asks for. A disclosure is where detail
                 * goes, never where a correction goes.
                 */}
                <AdvancedDisclosure
                  label="Detail"
                  summary="How reach is counted, and what happens to somebody on two lists"
                >
                  <div className="flex max-w-prose flex-col gap-3 text-[0.86rem] leading-relaxed text-muted">
                    <p>
                      Reach counts distinct {REACH_ADDRESS_PLURAL[channel]}, so a contact who is on
                      three of your lists is one person here and receives one message.
                    </p>
                    <p>
                      A campaign reaches only the people it can reach on its own channel. Wend never
                      falls back to the other one to make the number bigger, which is why the same
                      segment reaches a different number of people by email and by WhatsApp.
                    </p>
                    <p>
                      Once a segment is chosen, its own totals appear under the figure in small
                      type, so this number can be reconciled against the segment screen rather than
                      doubted.
                    </p>
                  </div>
                </AdvancedDisclosure>
              </CardContent>
            </Card>

            <SendGuardAlwaysOnNotice />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Button type="submit" loading={pending}>
            <Plus aria-hidden="true" className="size-4" />
            {pending ? 'Creating the draft' : 'Create draft'}
          </Button>

          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            Creates a draft and sends nothing. The campaign's own screen counts how many of
            {reach === null ? ' these people' : ` these ${formatCount(reach.reachable)} people`} will
            actually receive it, and offers Schedule there.
          </p>
        </div>
      </form>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Pickers                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Approved email templates only.
 *
 * The empty state is the interesting one: "no templates" here almost always
 * means "no *approved* templates", and sending someone to look for a list they
 * can see is not the same as telling them their template needs approving.
 */
function TemplateField({
  templates,
  total,
  loading,
  failure,
  onRetry,
  value,
  error,
  disabled,
  onChange,
  channel,
}: {
  templates: readonly TemplateSummary[];
  total: number;
  loading: boolean;
  /** The load's failure, when it left nothing to show. Null otherwise. */
  failure: unknown;
  onRetry: () => void;
  value: string | null;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
  channel: Channel;
}) {
  const options = useMemo<SelectOption[]>(
    () =>
      templates.map((template) => ({
        value: template.id,
        label: template.name,
        ...(template.subject === null ? {} : { description: template.subject }),
      })),
    [templates],
  );

  const label = channel === 'whatsapp' ? 'WhatsApp template' : 'Email template';

  return (
    <FormField
      label={label}
      required
      description={
        channel === 'whatsapp'
          ? 'Only templates Meta has approved can be sent, so only they are listed.'
          : 'Only approved templates can be sent, so only they are listed.'
      }
      error={error}
    >
      {loading ? (
        <Spinner size="sm" label="Loading your approved templates" />
      ) : failure !== null ? (
        <ErrorState
          error={failure}
          title="Could not load your templates"
          onRetry={onRetry}
          className="flex flex-col items-start gap-2 py-1"
        />
      ) : options.length === 0 ? (
        <p className="text-[0.88rem] leading-relaxed text-muted">
          {channel === 'whatsapp'
            ? 'No WhatsApp template here has been approved by Meta yet —'
            : 'No email template here has been approved yet, and only approved ones can be sent —'}{' '}
          <Link to="/templates" className="text-text underline underline-offset-4">
            open Templates
          </Link>{' '}
          to {channel === 'whatsapp' ? 'submit one to Meta' : 'finish one and approve it'}.
        </p>
      ) : (
        <>
          <Select
            options={options}
            value={value ?? undefined}
            onValueChange={onChange}
            disabled={disabled}
            placeholder="Choose an approved template"
            aria-label={label}
          />
          {total > options.length && (
            <p className="mt-1.5 text-[0.78rem] text-muted">
              The {formatCount(options.length)} most recently edited are listed, of{' '}
              {formatCount(total)}.
            </p>
          )}
        </>
      )}
    </FormField>
  );
}

/**
 * The audience.
 *
 * The option description carries the segment's *email* reach rather than its
 * size, so the list itself is honest before anything is selected: choosing
 * between "1,278 people" and "526 by email" in a dropdown is the same decision
 * the reach panel exists to inform, one moment earlier.
 */
function SegmentField({
  segments,
  total,
  loading,
  failure,
  onRetry,
  value,
  error,
  disabled,
  onChange,
  channel,
}: {
  segments: readonly PickableSegment[];
  total: number;
  loading: boolean;
  /** The load's failure, when it left nothing to show. Null otherwise. */
  failure: unknown;
  onRetry: () => void;
  value: string | null;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
  channel: Channel;
}) {
  // Per channel, because the two counts differ on the same segment: the email
  // one counts addresses, the WhatsApp one counts phone numbers. A single figure
  // beside a segment name would be right for one channel and an overstatement
  // for the other.
  const options = useMemo<SelectOption[]>(
    () =>
      segments.map((segment) => ({
        value: segment.id,
        label: segment.name,
        description: `${pluralise(campaignReach(segment.counts, channel).reachable, 'person', 'people')} reachable by ${CHANNEL_LABEL[channel]}`,
      })),
    [segments, channel],
  );

  return (
    <FormField
      label="Segment"
      required
      description={
        channel === 'whatsapp'
          ? 'Everyone in it who has a phone number will be a recipient, each of them once.'
          : 'Everyone in it who has an email address will be a recipient, each of them once.'
      }
      error={error}
    >
      {loading ? (
        <Spinner size="sm" label="Loading your segments" />
      ) : failure !== null ? (
        <ErrorState
          error={failure}
          title="Could not load your segments"
          onRetry={onRetry}
          className="flex flex-col items-start gap-2 py-1"
        />
      ) : options.length === 0 ? (
        <p className="text-[0.88rem] leading-relaxed text-muted">
          There are no segments in this workspace yet.{' '}
          <Link to="/segments/new" className="text-text underline underline-offset-4">
            Build one
          </Link>{' '}
          from the lists you have uploaded.
        </p>
      ) : (
        <>
          <Select
            options={options}
            value={value ?? undefined}
            onValueChange={onChange}
            disabled={disabled}
            placeholder="Choose a segment"
            aria-label="Segment"
          />
          {total > options.length && (
            <p className="mt-1.5 text-[0.78rem] text-muted">
              The first {formatCount(options.length)} by name are listed, of {formatCount(total)}.
            </p>
          )}
        </>
      )}
    </FormField>
  );
}

/**
 * The identities AWS has verified on the tenant's own account.
 *
 * Shown because `from_address_not_verified` is a blocker, and the repair for it
 * is a fact only AWS holds. An `EMAIL_ADDRESS` identity is an address and can
 * fill the field; a domain identity covers every address at that domain, so it
 * is named rather than offered — Wend has no way to know which mailbox there a
 * person means.
 *
 * A paused or unverified identity is not listed at all. Slice 3 caught this one
 * level up: AWS pauses individual identities, and a paused one delivers exactly
 * as much as an unverified one, so offering it would be offering a from address
 * that cannot send.
 */
function VerifiedIdentities({
  identities,
  checked,
  lastCheckFailed,
  connected,
  loading,
  failure,
  onRetry,
  disabled,
  onUse,
}: {
  identities: readonly SesIdentity[] | null;
  /** Whether a connection test has ever come back for this account. */
  checked: boolean;
  /**
   * Whether the latest test failed — the row's status, not the check, which a
   * failed test leaves null or stale. See `CHECK_FAILED_DISPLAY`.
   */
  lastCheckFailed: boolean;
  connected: boolean;
  loading: boolean;
  /**
   * The integration read's failure, when it left nothing to show. Null
   * otherwise — **and it is checked before `connected`**, which is the whole
   * point of it existing. See below.
   */
  failure: unknown;
  onRetry: () => void;
  disabled: boolean;
  onUse: (address: string) => void;
}) {
  if (loading) return <Spinner size="sm" label="Checking your sending account" />;

  /*
   * A FAILED READ IS NOT AN ANSWER ABOUT THE ACCOUNT.
   *
   * `connected` is `integration.data !== null`, and `data` is null both when the
   * workspace has no SES account and when the request never came back. Before
   * this branch existed the second case fell into the first, so a workspace with
   * a working, verified, production SES account was told — in a warning callout,
   * on the screen where it is about to build a campaign — that it had no sending
   * account and should go and connect one. That is the "zero versus absent"
   * failure with a link attached to it.
   *
   * It is an `ErrorState` rather than a `Callout`: this is a screen that could
   * not load what it went to fetch, it carries the request id, and it offers the
   * retry that the connect link is not.
   */
  if (failure !== null) {
    return (
      <ErrorState
        error={failure}
        title="Could not check your sending account"
        onRetry={onRetry}
        className="flex flex-col items-start gap-2 py-1"
      />
    );
  }

  if (!connected) {
    return (
      <Callout tone="warn" title="No sending account is connected">
        A campaign cannot be scheduled until this workspace has connected an Amazon SES account.{' '}
        <Link to={integrationTabPath('email')} className="text-text underline underline-offset-4">
          Connect one
        </Link>{' '}
        — you can finish this draft first either way.
      </Callout>
    );
  }

  /*
   * NEVER CHECKED IS NOT "CANNOT LIST".
   *
   * `lastCheck` is null for a connected account whose connect-time check failed
   * — a wrong secret, a wrong region, a timeout — and `lastCheck?.identities` is
   * then null too, which is exactly what a key refused `ses:ListEmailIdentities`
   * produces. Before this branch the first case fell into the second, and a
   * tenant who had pasted a broken secret was told, calmly, that the key merely
   * lacked one permission and they could type any from address. Asked first, in
   * the Overview's and the Email tab's own words for an untested account.
   */
  if (lastCheckFailed) {
    return (
      <Callout tone="warn" title={CHECK_FAILED_DISPLAY.title.email}>
        The latest test of these credentials did not succeed, so Wend cannot say whether they work,
        let alone which senders are verified.{' '}
        <Link to={integrationTabPath('email')} className="text-text underline underline-offset-4">
          See why on Integrations
        </Link>{' '}
        — you can finish this draft first either way.
      </Callout>
    );
  }

  if (!checked) {
    return (
      <Callout tone={SES_NOT_TESTED.calloutTone} title={SES_NOT_TESTED.title}>
        No connection test has come back for this account, so Wend cannot say whether these
        credentials work, let alone which senders are verified.{' '}
        <Link to={integrationTabPath('email')} className="text-text underline underline-offset-4">
          Run the test on Integrations
        </Link>{' '}
        — you can finish this draft first either way.
      </Callout>
    );
  }

  /*
   * `null` is not `[]`, and the difference decides which of two opposite
   * sentences a person reads.
   *
   * `(identities ?? [])` collapsed them, so a key without
   * `ses:ListEmailIdentities` fell through to "nothing on this account can
   * send yet — any from address will be refused", which is a claim about an
   * account nobody was allowed to read, on the screen where somebody is about
   * to choose a from address. It would send them to fix a sender that is
   * already verified.
   */
  if (identities === null) {
    return (
      <Callout tone="info" title="Wend cannot list this account's senders">
        This key cannot call ses:ListEmailIdentities, so there is nothing to pick from here — type
        the from address yourself. AWS checks it is verified when the campaign sends.
      </Callout>
    );
  }

  const usable = identities.filter((identity) => identity.verified && identity.sendingEnabled);

  if (usable.length === 0) {
    return (
      <Callout tone="warn" title="Nothing on this AWS account can send yet">
        Your SES account has no identity that is both verified and sending — nothing is verified, or
        AWS has paused the one that is. Any from address will be refused until that is fixed.
      </Callout>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Kicker rule>
        Verified on your AWS account
      </Kicker>
      <ul className="flex flex-wrap gap-2">
        {usable.map((identity) => (
          <li key={identity.identity}>
            {identity.identityType === 'EMAIL_ADDRESS' ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  onUse(identity.identity);
                }}
              >
                {identity.identity}
              </Button>
            ) : (
              <span className="inline-flex h-8 items-center rounded-full border border-line px-3.5 font-mono text-[0.75rem] text-muted">
                {identity.identity}
                <span className="ml-2 text-muted">
                  {identityTypeLabel(identity.identityType).toLowerCase()}
                </span>
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="text-[0.8rem] leading-relaxed text-muted">
        A verified domain covers every address at it, so pick any mailbox you actually read.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Timing                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * One of the two timing choices.
 *
 * A real `<input type="radio">` rather than two styled buttons: it is a choice
 * between mutually exclusive options, and the radio group is what gives arrow-key
 * navigation and the right announcement for free.
 */
function TimingChoice({
  checked,
  onSelect,
  label,
  description,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-base border border-line bg-white/[0.02] px-3.5 py-3 hover:border-line-strong">
      <input
        type="radio"
        name="campaign-timing"
        className="mt-1 size-4 shrink-0 accent-brand-b"
        checked={checked}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[0.9rem] text-text">
          <Clock aria-hidden="true" className="size-3.5 text-muted" />
          {label}
        </span>
        <span className="mt-0.5 block text-[0.82rem] leading-snug text-muted">{description}</span>
      </span>
    </label>
  );
}
